import { useMemo } from "react"
import { type Query as CubeQuery, type Filter } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { getPivotData } from "~/ui-rtk/utils/cube"
import {
  CUBE_NAME,
  TRIGGERED_PATTERNS,
  SOCIAL_REFERRAL_SOURCES,
  MAX_NODES_PER_STEP,
} from "./constants"
import type {
  ChannelTab,
  SessionFilterOption,
  GranularityOption,
  StepData,
  StepNode,
} from "./types"

function buildChannelFilter(channel: ChannelTab): Filter[] | null {
  const src = `${CUBE_NAME}.session_source`
  const med = `${CUBE_NAME}.session_medium`

  switch (channel) {
    case "total":
      return null

    case "baseline":
      return [
        {
          or: [
            { member: src, operator: "equals", values: ["(direct)"] },
            { member: med, operator: "equals", values: ["organic"] },
            {
              and: [
                { member: med, operator: "equals", values: ["referral"] },
                {
                  or: SOCIAL_REFERRAL_SOURCES.map(domain => ({
                    member: src,
                    operator: "contains" as const,
                    values: [domain],
                  })),
                },
              ],
            },
          ],
        },
      ]

    case "triggered":
      return [
        {
          or: [
            ...TRIGGERED_PATTERNS.map(p => ({
              member: med,
              operator: "contains" as const,
              values: [p],
            })),
            ...TRIGGERED_PATTERNS.map(p => ({
              member: src,
              operator: "contains" as const,
              values: [p],
            })),
          ],
        },
      ]

    case "paid":
      // NOT baseline AND NOT triggered
      return [
        { member: src, operator: "notEquals", values: ["(direct)"] },
        { member: med, operator: "notEquals", values: ["organic"] },
        // Exclude social referral (medium=referral + social domain source)
        {
          or: [
            { member: med, operator: "notEquals", values: ["referral"] },
            {
              and: SOCIAL_REFERRAL_SOURCES.map(domain => ({
                member: src,
                operator: "notContains" as const,
                values: [domain],
              })),
            },
          ],
        },
        // Exclude triggered patterns from both medium and source
        ...TRIGGERED_PATTERNS.map(p => ({
          member: med,
          operator: "notContains" as const,
          values: [p],
        })),
        ...TRIGGERED_PATTERNS.map(p => ({
          member: src,
          operator: "notContains" as const,
          values: [p],
        })),
      ]

    default:
      return null
  }
}

function buildStepQuery(
  companyId: string,
  stepNumber: number,
  granularity: GranularityOption,
  channel: ChannelTab,
  sessionFilter: SessionFilterOption,
  dateRange: [string, string],
  step1Selection?: string,
  step2Selection?: string,
): CubeQuery {
  const filters: Filter[] = [
    {
      member: `${CUBE_NAME}.companyid`,
      operator: "equals",
      values: [companyId],
    },
    {
      member: `${CUBE_NAME}.step_number`,
      operator: "equals",
      values: [String(stepNumber)],
    },
    {
      member: `${CUBE_NAME}.granularity`,
      operator: "equals",
      values: [granularity],
    },
    {
      member: `${CUBE_NAME}.page_count`,
      operator: "gte",
      values: ["1"],
    },
  ]

  // Channel filter using session_source + session_medium
  const channelFilters = buildChannelFilter(channel)
  if (channelFilters) {
    filters.push(...channelFilters)
  }

  // Session outcome filter – columns are UInt8 (0/1), not string booleans
  if (sessionFilter === "with_purchases") {
    filters.push({
      member: `${CUBE_NAME}.had_purchase`,
      operator: "equals",
      values: ["1"],
    })
  } else if (sessionFilter === "with_adds_to_cart") {
    filters.push({
      member: `${CUBE_NAME}.had_add_to_cart`,
      operator: "equals",
      values: ["1"],
    })
  }

  // Drill-down filters: use step_1/step_2 page_path columns
  if (stepNumber >= 2 && step1Selection) {
    filters.push({
      member: `${CUBE_NAME}.step_1_page_path`,
      operator: "equals",
      values: [step1Selection],
    })
  }

  if (stepNumber >= 3 && step2Selection) {
    filters.push({
      member: `${CUBE_NAME}.step_2_page_path`,
      operator: "equals",
      values: [step2Selection],
    })
  }

  return {
    dimensions: [`${CUBE_NAME}.page_path`],
    measures: [
      `${CUBE_NAME}.sessions_count`,
      `${CUBE_NAME}.purchases`,
      `${CUBE_NAME}.new_orders`,
      `${CUBE_NAME}.current_revenue`,
      `${CUBE_NAME}.new_customer_revenue`,
      `${CUBE_NAME}.prediction`,
    ],
    filters,
    timeDimensions: [
      {
        dimension: `${CUBE_NAME}.md_date`,
        dateRange,
      },
    ],
    order: {
      [`${CUBE_NAME}.sessions_count`]: "desc",
    },
    limit: MAX_NODES_PER_STEP,
  } as CubeQuery
}

function parseStepResult(
  resultSet: ReturnType<typeof useCubeQuery>["resultSet"],
  query: CubeQuery,
): StepNode[] {
  if (!resultSet) return []

  const pivotData = getPivotData(resultSet, query)

  return pivotData.map((row: Record<string, unknown>) => ({
    pagePath: String(row[`${CUBE_NAME}.page_path`] || "Unknown"),
    sessionsCount: Number(row[`${CUBE_NAME}.sessions_count`] || 0),
    orders: Number(row[`${CUBE_NAME}.purchases`] || 0),
    newOrders: Number(row[`${CUBE_NAME}.new_orders`] || 0),
    revenue: Number(row[`${CUBE_NAME}.current_revenue`] || 0),
    newCustomerRevenue: Number(row[`${CUBE_NAME}.new_customer_revenue`] || 0),
    predictedLtv: Number(row[`${CUBE_NAME}.prediction`] || 0),
  }))
}

export default function useConnect(
  channel: ChannelTab,
  sessionFilter: SessionFilterOption,
  granularity: GranularityOption,
  dateRange: [string, string],
  selectedNodes: string[],
  visibleSteps: number,
) {
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id || ""

  // Step 1 query
  const step1Query = useMemo(
    () =>
      buildStepQuery(
        companyId,
        1,
        granularity,
        channel,
        sessionFilter,
        dateRange,
      ),
    [companyId, granularity, channel, sessionFilter, dateRange],
  )

  // Step 2 query – filter by step 1 selection
  const step2Query = useMemo(
    () =>
      buildStepQuery(
        companyId,
        2,
        granularity,
        channel,
        sessionFilter,
        dateRange,
        selectedNodes[0],
      ),
    [
      companyId,
      granularity,
      channel,
      sessionFilter,
      dateRange,
      selectedNodes[0],
    ],
  )

  // Step 3 query – filter by step 1 AND step 2 selections
  const step3Query = useMemo(
    () =>
      buildStepQuery(
        companyId,
        3,
        granularity,
        channel,
        sessionFilter,
        dateRange,
        selectedNodes[0],
        selectedNodes[1],
      ),
    [
      companyId,
      granularity,
      channel,
      sessionFilter,
      dateRange,
      selectedNodes[0],
      selectedNodes[1],
    ],
  )

  const { resultSet: step1Result, isLoading: isStep1Loading } = useCubeQuery(
    step1Query,
    { skip: !companyId },
  )

  const { resultSet: step2Result, isLoading: isStep2Loading } = useCubeQuery(
    step2Query,
    {
      skip: !companyId || !selectedNodes[0] || visibleSteps < 2,
    },
  )

  const { resultSet: step3Result, isLoading: isStep3Loading } = useCubeQuery(
    step3Query,
    {
      skip: !companyId || !selectedNodes[1] || visibleSteps < 3,
    },
  )

  const step1Nodes = useMemo(
    () => parseStepResult(step1Result, step1Query),
    [step1Result, step1Query],
  )

  const step2Nodes = useMemo(
    () => parseStepResult(step2Result, step2Query),
    [step2Result, step2Query],
  )

  const step3Nodes = useMemo(
    () => parseStepResult(step3Result, step3Query),
    [step3Result, step3Query],
  )

  const stepsData: StepData[] = useMemo(() => {
    const steps: StepData[] = [
      { stepNumber: 1, nodes: step1Nodes, isLoading: isStep1Loading },
    ]

    if (visibleSteps >= 2) {
      steps.push({
        stepNumber: 2,
        nodes: step2Nodes,
        isLoading: isStep2Loading,
      })
    }

    if (visibleSteps >= 3) {
      steps.push({
        stepNumber: 3,
        nodes: step3Nodes,
        isLoading: isStep3Loading,
      })
    }

    return steps
  }, [
    step1Nodes,
    step2Nodes,
    step3Nodes,
    isStep1Loading,
    isStep2Loading,
    isStep3Loading,
    visibleSteps,
  ])

  return {
    stepsData,
    isLoading: isStep1Loading,
  }
}
